import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import type { CourseModule } from '../../../types/course';

interface ModuleFormProps {
  onSubmit: (moduleData: Partial<CourseModule>) => Promise<void>;
  onCancel: () => void;
  initialData?: CourseModule;
}

export function ModuleForm({ onSubmit, onCancel, initialData }: ModuleFormProps) {
  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    description: initialData?.description || '',
    duration: initialData?.duration || '',
    order: initialData?.order || 1,
    status: initialData?.status || 'draft'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Module title is required');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-white mb-2">Title</label>
        <input
          type="text"
          value={formData.title}
          onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
          className="input w-full"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-white mb-2">Description</label>
        <textarea
          value={formData.description}
          onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
          className="input w-full h-32"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-white mb-2">Duration</label>
        <input
          type="text"
          value={formData.duration}
          onChange={e => setFormData(prev => ({ ...prev, duration: e.target.value }))}
          className="input w-full"
          placeholder="e.g. 2 weeks"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-white mb-2">Order</label>
        <input
          type="number"
          value={formData.order}
          onChange={e => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) }))}
          className="input w-full"
          min="1"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-white mb-2">Status</label>
        <select
          value={formData.status}
          onChange={e => setFormData(prev => ({ ...prev, status: e.target.value }))}
          className="input w-full"
        >
          <option value="draft">Draft</option>
          <option value="published">Published</option>
        </select>
      </div>

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-400 hover:text-white"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="btn-primary"
        >
          {isSubmitting ? 'Saving...' : initialData ? 'Update Module' : 'Create Module'}
        </button>
      </div>
    </form>
  );
}